import React from 'react';
import { MenuItem, TextField } from '@mui/material';

/**
 * The Horizon form-control tokens at filter size.
 *
 * Same border, radius, shadow and focus ring as the panel's asset picker and the shared
 * DateRangePicker — 8px radius, Gray/300 border, shadow-xs — but 36px tall rather than 44px,
 * because these sit inside a card header beside a title rather than in the filter bar.
 */
const selectSx = {
    minWidth: '9.5rem',
    '& .MuiOutlinedInput-root': {
        minHeight: '2.25rem',
        borderRadius: '0.5rem',
        background: '#FFFFFF',
        boxShadow: '0rem 0.0625rem 0.125rem rgba(16, 24, 40, 0.05)',
        fontFamily: 'Inter',
        fontSize: '0.8125rem',
        color: '#101828',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#73A2BA', borderWidth: '0.0625rem' },
    '& .MuiSelect-select': { padding: '0.375rem 0.75rem' },
};

const menuProps = {
    PaperProps: {
        sx: {
            borderRadius: '0.5rem',
            marginTop: '0.25rem',
            boxShadow: '0rem 0.75rem 1rem -0.25rem rgba(16, 24, 40, 0.08), 0rem 0.25rem 0.375rem -0.125rem rgba(16, 24, 40, 0.03)',
            '& .MuiMenuItem-root': {
                fontFamily: 'Inter',
                fontSize: '0.8125rem',
                color: '#101828',
            },
            '& .Mui-selected': { background: '#F2F4F7' },
        },
    },
};

/**
 * One labelled filter select, styled as a Horizon form control.
 *
 * The label sits beside the control rather than above it — these live in a card header next to a
 * title, where a stacked label would push the header two lines tall. `''` is the "no filter"
 * value throughout, so a caller tests `filter && ...` rather than juggling a sentinel.
 *
 * **`displayEmpty` is load-bearing.** MUI reads `''` as "nothing selected" and renders a
 * zero-width space instead of the option that matches it, so every one of these filters opened
 * blank — the Safety and Idle tabs gave no clue that they default to showing everything, and a
 * user could not tell an unset filter from a broken one. With it, `''` resolves to its own
 * MenuItem like any other value, and the control always states what it is showing.
 */
const OdSelect = (props) => {
    const {
        label, value, onChange, options, ariaLabel,
    } = props;

    return (
        // eslint-disable-next-line jsx-a11y/label-has-associated-control
        <label className="od-select">
            <span className="od-select-label">{label}</span>
            <TextField
              select
              size="small"
              value={value}
              onChange={(event) => onChange(event.target.value)}
              sx={selectSx}
              SelectProps={{ MenuProps: menuProps, displayEmpty: true }}
              inputProps={{ 'aria-label': ariaLabel || label }}
            >
                {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
            </TextField>
        </label>
    );
};

export default OdSelect;
